<template>
  <div v-if="!isMobile()" class="card">
    <div v-if="!hasVitals">
      <p>No vital signs yet, you can:</p>
      <ul>
        <li>
          <button type="button" @click="openVitalsModal" class="add-vitals-link">
            add entries manually
          </button>
        </li>
        <li><router-link to="/cdas">upload CDA with vitals</router-link></li>
      </ul>
    </div>

    <div v-else>
      <div class="card-header">
        <togglable-caret-header
          ref="vital-header"
          :data-bs-target="'#' + toggleTarget"
          :showCaret="hasVitals"
        >
          <a>Vital Signs</a>
        </togglable-caret-header>
        <div class="header-buttons">
          <button type="button" @click.stop.prevent="openVitalsModal" class="add-vitals-link-header">
            <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />
            Add entries manually
          </button>
        </div>
      </div>

      <div :id="toggleTarget" class="collapse show">
        <div class="card-body">
          <measure-grid
            :selected="selected"
            :range="range"
            :grid="measAndDateGrid"
            :dates="datesForGrid"
            :isVitalSign="true"
            :isGrouping="grouping != 'all'"
          />
        </div>
      </div>
    </div>
  </div>
  <div v-else class="mobile-vitals">
    <div v-if="!hasVitals" class="mobile-vitals-empty">
      <p>No vital signs yet, you can:</p>
      <ul>
        <li>
          <button type="button" @click="openVitalsModal" class="add-vitals-link">
            add entries manually
          </button>
        </li>
        <li><router-link to="/cdas">upload CDA with vitals</router-link></li>
      </ul>
    </div>
    <template v-else>
      <div class="mobile-vitals-actions">
        <button type="button" @click="openVitalsModal" class="add-vitals-link-header">
          <font-awesome-icon :icon="['fas', 'plus-circle']" class="mr-1" />
          Add entries manually
        </button>
      </div>
      <measure-grid
        :range="range"
        :grid="measAndDateGrid"
        :dates="datesForGrid"
        :isVitalSign="true"
        :isGrouping="grouping != 'all'"
      />
    </template>
  </div>

  <div v-if="showVitalsModal" class="vitals-modal-overlay" @click="closeVitalsModal">
    <div class="vitals-modal-content" @click.stop>
      <div class="vitals-modal-header">
        <h3>
          <font-awesome-icon :icon="['fas', 'notes-medical']" class="modal-title-icon" />
          Add Vital Signs
        </h3>
        <button type="button" class="modal-close-btn" @click="closeVitalsModal">
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>

      <div class="vitals-modal-body" v-if="vitalsFormData">
        <form @submit.prevent="submitVitals" class="vitals-form">
          <div class="form-group">
            <label>Glucose - Finger Stick (mg/dL)</label>
            <input
              v-model.number="vitalsFormData.glucose"
              type="number"
              placeholder="Glucose level"
              min="0"
              max="600"
              step="0.1"
            />
          </div>

          <div class="form-group">
            <label>Blood Pressure</label>
            <div class="bp-inputs">
              <input
                v-model.number="vitalsFormData.systolic"
                type="number"
                placeholder="Systolic"
                min="0"
                max="300"
              />
              <span class="bp-separator">/</span>
              <input
                v-model.number="vitalsFormData.diastolic"
                type="number"
                placeholder="Diastolic"
                min="0"
                max="200"
              />
              <span class="bp-unit">mmHg</span>
            </div>
          </div>

          <div class="form-group">
            <label>Heart Rate (bpm)</label>
            <input
              v-model.number="vitalsFormData.heart_rate"
              type="number"
              placeholder="Heart rate"
              min="0"
              max="250"
            />
          </div>

          <div class="form-group">
            <label>Oxygen Saturation (%)</label>
            <input
              v-model.number="vitalsFormData.oxygen_saturation"
              type="number"
              placeholder="SpO2"
              min="0"
              max="100"
              step="0.1"
            />
          </div>

          <div class="form-group">
            <label>Weight (lbs)</label>
            <input
              v-model.number="vitalsFormData.weight"
              type="number"
              placeholder="Weight"
              min="0"
              step="0.1"
            />
          </div>

          <div class="form-group">
            <label>Temperature (°F)</label>
            <input
              v-model.number="vitalsFormData.temperature"
              type="number"
              placeholder="Temperature"
              min="90"
              max="110"
              step="0.1"
            />
          </div>

          <div class="form-group">
            <label>Measurement Time</label>
            <input v-model="vitalsFormData.measurement_time" type="datetime-local" />
          </div>

          <div class="form-group">
            <label>Notes (optional)</label>
            <textarea
              v-model="vitalsFormData.notes"
              rows="3"
              placeholder="Any additional notes..."
            />
          </div>

          <div class="form-actions">
            <button type="button" class="btn-secondary" @click="closeVitalsModal">
              Cancel
            </button>
            <button type="submit" class="btn-primary" :disabled="loggingVitals">
              <font-awesome-icon v-if="loggingVitals" :icon="['fas', 'spinner']" spin />
              <font-awesome-icon v-else :icon="['fas', 'save']" />
              {{ loggingVitals ? 'Saving...' : 'Save Vitals' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import axios from 'axios';
import EventBus from '@/event-bus';
import {
  getDescByCcd,
  getMeasAndDateGridLimited,
  getCondensedDatesForGridLimited,
  getCondensedDatesForGridRange,
  getMeasAndDateGridRange,
  getMeasureWeightByCcd,
} from '@/data/getters';
import { requestExternalMeasures } from '@/api/dataApi';
import { server } from '@/api/SERVER_ADDR';
import TogglableCaretHeader from '@/components/includes/TogglableCaretHeader';
import MeasureGrid from './MeasuresGrid';

const EMPTY_VITALS_FORM = () => ({
  glucose: null,
  systolic: null,
  diastolic: null,
  heart_rate: null,
  oxygen_saturation: null,
  weight: null,
  temperature: null,
  measurement_time: '',
  notes: '',
});

export default {
  name: 'vital-signs',
  components: {
    'togglable-caret-header': TogglableCaretHeader,
    'measure-grid': MeasureGrid,
  },
  props: {
    grouping: {
      type: String,
      required: true,
    },
    selected: {
      default: null,
    },
    range: {
      type: Number,
      default: 50000,
    },
    start: {
      type: String,
      default: null,
    },
    end: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      toggleHeading: 'vital-signs-heading',
      toggleTarget: 'vital-signs',
      checked: '',
      // Group tabs used to hardcode LA_labccd.id values (e.g. 77371 = SBP).
      // Those serial ids drift after catalog reseeds — filter by stable LOINC instead.
      // Keys match /vitalsGroup/:grouping route params (BP tab uses "pulse").
      vitalGroupLoincs: {
        glucose: ['2345-7', '41653-7'], // CDA serum + legacy fingerstick
        pulse: ['8480-6', '8462-4', '8867-4'], // SBP, DBP, HR
        weight: ['29463-7', '3141-9', '39156-5', '8302-2'], // CDA body wt + legacy measured
        breathing: ['2710-2', '59408-5', '59417-6', '9279-1'], // SpO2 variants + RR
        temperature: ['8310-5'],
      },
      // Manual Fora used to write alternate LOINCs; collapse those into the CDA row.
      vitalLoincFamilies: [
        { preferred: '2345-7', aliases: ['2345-7', '41653-7'] },
        { preferred: '29463-7', aliases: ['29463-7', '3141-9'] },
        { preferred: '59408-5', aliases: ['59408-5', '59417-6', '2710-2'] },
      ],
      showVitalsModal: false,
      loggingVitals: false,
      vitalsFormData: EMPTY_VITALS_FORM(),
    };
  },
  mounted() {
    if (this.$route.query.open === 'vitals' && this.$refs['vital-header']) {
      this.$refs['vital-header'].$el.click();
    }
  },
  computed: {
    ...mapGetters('login', {
      allData: 'getAllData',
    }),
    ...mapGetters('login/watch', {
      measuresWatching: 'getMeasuresWatching',
      proceduresWatching: 'getProceduresWatching',
      checkedMeasures: 'getCheckedMeasures',
      vitalCcds: 'getVitalSignCodes',
    }),
    // allData with alias LOINC measure ids folded into the preferred CCD bucket.
    displayAllData() {
      const base = this.allData;
      // Nothing to merge until measCcdMeasIds is loaded.
      if (!base?.measCcdMeasIds) return base;

      const mergedIds = {};
      const src = base.measCcdMeasIds;
      // Re-bucket every CCD's measure ids under its canonical (CDA) CCD.
      for (const key of Object.keys(src)) {
        const rawCcd = parseInt(key, 10);
        if (isNaN(rawCcd)) continue; // skip non-numeric keys
        const canon = this.canonicalCcdId(rawCcd);
        const bucket = String(canon);
        if (!mergedIds[bucket]) mergedIds[bucket] = [];
        const ids = src[key] ?? src[String(rawCcd)] ?? [];
        // Dedupe measure ids when both alias and preferred already listed them.
        for (const id of ids) {
          if (!mergedIds[bucket].includes(id)) mergedIds[bucket].push(id);
        }
      }
      // Keep the rest of allData; only swap the merged index.
      return { ...base, measCcdMeasIds: mergedIds };
    },
    vitalSigns() {
      const vitals = [];
      const meas = this.allData?.measures || {};
      // Catalog of CCD ids flagged as vital signs from the session API.
      const vitalCodeSet = new Set(
        (this.vitalCcds || []).map((code) => parseInt(code, 10)).filter((code) => !isNaN(code))
      );
      for (const measId in meas) {
        const code = parseInt(meas[measId].codes?.[0], 10);
        if (!isNaN(code) && vitalCodeSet.has(code)) {
          vitals.push(meas[measId]);
        }
      }
      return vitals;
    },
    // CCD ids to show as rows — aliases collapsed so Gluc/Wt/SpO2 share one row.
    vitalSignCcds() {
      const vitalCodeSet = new Set(
        (this.vitalCcds || []).map((code) => parseInt(code, 10)).filter((code) => !isNaN(code))
      );
      const result = new Set();

      // Collect from measure objects, then canonicalize LOINC aliases.
      for (const measId in this.allData?.measures || {}) {
        const code = parseInt(this.allData.measures[measId].codes?.[0], 10);
        if (!isNaN(code) && vitalCodeSet.has(code)) {
          result.add(this.canonicalCcdId(code));
        }
      }

      // Also collect from the CCD→measureId index (covers sparse caches).
      for (const ccdKey in this.allData?.measCcdMeasIds || {}) {
        const code = parseInt(ccdKey, 10);
        const ids =
          this.allData.measCcdMeasIds[ccdKey]
          ?? this.allData.measCcdMeasIds[String(code)]
          ?? [];
        if (!isNaN(code) && vitalCodeSet.has(code) && ids.length > 0) {
          result.add(this.canonicalCcdId(code));
        }
      }

      let codes = [...result];
      // Tab filter: keep only CCDs whose LOINC is in this grouping.
      if (this.grouping !== 'all') {
        const allowedLoincs = new Set(this.vitalGroupLoincs[this.grouping] || []);
        codes = codes.filter((ccdId) => allowedLoincs.has(this.loincForCcd(ccdId)));
      }
      return codes;
    },
    // True when at least one merged vital row has measure ids.
    hasVitals() {
      if (!this.displayAllData?.measures) {
        return false;
      }
      return this.vitalSignCcds.some((ccd) => {
        const ids =
          this.displayAllData.measCcdMeasIds?.[ccd]
          ?? this.displayAllData.measCcdMeasIds?.[String(ccd)]
          ?? [];
        return ids.length > 0;
      });
    },
    // Build the date×measure matrix using merged (alias-collapsed) data.
    measAndDateGrid() {
      if (this.start && this.end) {
        const st = new Date(this.start);
        const end = new Date(this.end);
        return getMeasAndDateGridRange(this.displayAllData, this.vitalSignCcds, st, end);
      }
      const today = Date.now();
      const limit = new Date(today - 1000 * 60 * 60 * 24 * this.range);
      return getMeasAndDateGridLimited(this.displayAllData, this.vitalSignCcds, limit);
    },
    // Column dates for the grid (same merge path as measAndDateGrid).
    datesForGrid() {
      if (this.start && this.end) {
        const st = new Date(this.start);
        const end = new Date(this.end);
        return getCondensedDatesForGridRange(this.displayAllData, this.vitalSignCcds, st, end);
      }
      const today = Date.now();
      const limit = new Date(today - 1000 * 60 * 60 * 24 * this.range);
      return getCondensedDatesForGridLimited(this.displayAllData, this.vitalSignCcds, limit);
    },
  },
  beforeDestroy() {
    delete this.vitalSigns;
    delete this.vitalSignCcds;
  },
  methods: {
    ...mapActions('login/watch', [
      'removeMeasure',
      'addCheckedMeasure',
      'removeCheckedMeasure',
    ]),
    ...mapActions('login', ['addMeasures']),
    // Resolve LA_labccd.id → LOINC/SNOMED string from session ccds map.
    loincForCcd(ccdId) {
      // Try number / string / Number keys — allData.ccds key type varies.
      const entry =
        this.allData?.ccds?.[ccdId]
        ?? this.allData?.ccds?.[String(ccdId)]
        ?? this.allData?.ccds?.[Number(ccdId)];
      return String(entry?.code || '').trim();
    },
    // Find catalog CCD id for a LOINC string (first match).
    ccdIdForLoinc(loinc) {
      const target = String(loinc || '').trim();
      if (!target) return null;
      // Linear scan is fine — ccds map is small for a patient session.
      for (const [id, entry] of Object.entries(this.allData?.ccds || {})) {
        if (String(entry?.code || '').trim() === target) {
          const n = parseInt(id, 10);
          return isNaN(n) ? null : n;
        }
      }
      return null; // LOINC not present in this environment's catalog
    },
    // Map alternate LOINCs (manual Fora) onto the preferred CDA CCD id.
    canonicalCcdId(ccdId) {
      const loinc = this.loincForCcd(ccdId);
      // Which alias family (glucose / weight / SpO2) does this LOINC belong to?
      const family = (this.vitalLoincFamilies || []).find((f) =>
        (f.aliases || []).includes(loinc)
      );
      if (!family) return ccdId; // not an alias — leave as-is
      // Prefer the CDA LOINC's CCD when it exists in the catalog.
      const preferredCcd = this.ccdIdForLoinc(family.preferred);
      if (preferredCcd != null) return preferredCcd;
      return ccdId; // preferred missing — keep the original CCD
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    },
    sortMeasCcds(measCcds) {
      const sortedMeasCcds = [];
      Object.keys(measCcds).forEach((measCcd) => {
        sortedMeasCcds.push({
          id: measCcd,
          desc: this.getDescByCcd(measCcd, this.allData),
          weight: getMeasureWeightByCcd(measCcd, this.allData),
        });
      });
      return _.orderBy(sortedMeasCcds, 'weight', 'desc');
    },
    getDescFromMeasures(measures) {
      return measures[Object.keys(measures)[0]].desc;
    },
    handleRemoveMeasure(procCcd, measCcd) {
      this.removeCheckedMeasure(measCcd);
      this.removeMeasure({ proc: procCcd, meas: measCcd });
    },
    handleCheckMeasure(measCcd) {
      if (this.checkedMeasures[measCcd]) {
        this.removeCheckedMeasure(measCcd);
      } else {
        this.addCheckedMeasure(measCcd);
      }
    },
    getDescByCcd,
    openVitalsModal() {
      this.resetVitalsForm();
      this.showVitalsModal = true;
    },
    closeVitalsModal() {
      this.showVitalsModal = false;
      this.resetVitalsForm();
    },
    resetVitalsForm() {
      this.vitalsFormData = {
        ...EMPTY_VITALS_FORM(),
        measurement_time: this.toLocalDatetimeLocalValue(new Date()),
      };
    },
    hasVitalValue(value) {
      return value !== null && value !== undefined && value !== '';
    },
    /** datetime-local needs local wall time (YYYY-MM-DDTHH:mm), not UTC from toISOString. */
    toLocalDatetimeLocalValue(date) {
      if (!(date instanceof Date) || isNaN(date.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return (
        `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}`
      );
    },
    normalizeMeasurementTime(measurementTime) {
      if (!measurementTime) {
        return this.toLocalDatetimeLocalValue(new Date());
      }
      if (measurementTime.includes('T') && measurementTime.includes('Z')) {
        return this.toLocalDatetimeLocalValue(new Date(measurementTime));
      }
      if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}/.test(measurementTime)) {
        return measurementTime.replace(' ', 'T').substring(0, 16);
      }
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(measurementTime)) {
        const date = new Date(measurementTime);
        if (!isNaN(date.getTime())) {
          return this.toLocalDatetimeLocalValue(date);
        }
      }
      return measurementTime;
    },
    emitVitalsSaved() {
      try {
        if (this.$vueEventBus && typeof this.$vueEventBus.$emit === 'function') {
          this.$vueEventBus.$emit('vitals-saved');
        } else if (EventBus && typeof EventBus.emit === 'function') {
          EventBus.emit('vitals-saved');
        }
      } catch (e) {
        console.warn('Could not emit vitals-saved event:', e);
      }
    },
    refreshMeasuresAfterSave() {
      setTimeout(() => {
        requestExternalMeasures({})
          .then((measuresData) => {
            if (!measuresData) return;
            this.addMeasures({
              externalMeasures: measuresData.externalMeasures || measuresData,
              externalMeasCcdMeasIds: measuresData.externalMeasCcdMeasIds || {},
              externalMeasCcds: measuresData.externalMeasCcds || [],
            });
            this.emitVitalsSaved();
          })
          .catch((error) => {
            console.error('Error refreshing measures data:', error);
          });
      }, 1500);
    },
    async submitVitals() {
      const form = this.vitalsFormData;
      const hasGlucose = this.hasVitalValue(form.glucose);
      const hasSystolic = this.hasVitalValue(form.systolic);
      const hasDiastolic = this.hasVitalValue(form.diastolic);
      const hasHeartRate = this.hasVitalValue(form.heart_rate);
      const hasOxygen = this.hasVitalValue(form.oxygen_saturation);
      const hasWeight = this.hasVitalValue(form.weight);
      const hasTemperature = this.hasVitalValue(form.temperature);

      if (
        !hasGlucose &&
        !hasSystolic &&
        !hasDiastolic &&
        !hasHeartRate &&
        !hasOxygen &&
        !hasWeight &&
        !hasTemperature
      ) {
        alert('Please enter at least one vital sign.');
        return;
      }

      this.loggingVitals = true;
      try {
        const externId = this.allData?.patient?.externId;
        if (!externId) {
          alert('Error: Patient ID not found. Please refresh the page.');
          return;
        }

        const vitalsContent = {
          Wellness: {
            externId,
            glucose_level: hasGlucose ? form.glucose : null,
            systolic: hasSystolic ? form.systolic : null,
            diastolic: hasDiastolic ? form.diastolic : null,
            heart_rate: hasHeartRate ? form.heart_rate : null,
            oxygen_saturation: hasOxygen ? form.oxygen_saturation : null,
            weight: hasWeight ? form.weight : null,
            temperature: hasTemperature ? form.temperature : null,
            measurement_time: this.normalizeMeasurementTime(form.measurement_time),
            notes: form.notes || '',
          },
        };

        const response = await axios.post(server + '/fora/manual-vitals', vitalsContent, {
          headers: { 'Content-Type': 'application/json' },
        });

        const responseStatus = response.data?.status || response.data?.data?.status;
        const responseMessage = response.data?.message || response.data?.data?.message;

        if (
          responseStatus === 'OK' ||
          responseStatus === 'ok' ||
          responseStatus === 'success'
        ) {
          alert('Vital signs saved successfully!');
          this.closeVitalsModal();
          this.$emit('vitals-updated');
          this.emitVitalsSaved();
          this.refreshMeasuresAfterSave();
        } else {
          alert('Error saving vitals: ' + (responseMessage || 'Please try again.'));
        }
      } catch (error) {
        console.error('Error saving vitals:', error);
        const errorMessage =
          error.response?.data?.message ||
          error.response?.data?.data?.message ||
          error.message ||
          'Please try again.';
        alert('Error saving vitals: ' + errorMessage);
      } finally {
        this.loggingVitals = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.watch-list-section-title {
  padding-left: 1em;
  font-weight: bold;
}
.list-group {
  padding: 0;
}
.proc-group {
  padding-left: 0;
}
.list-group-item {
  padding: 0.5rem 0.5rem 0.5rem 2rem;
  border: none;

  &:nth-child(odd) {
    background-color: rgba(0, 0, 0, 0.05);
  }
}
.card-header {
  padding: 0.2rem;
  background-color: #fff;
  display: flex;
  justify-content: space-between;
  align-items: center;

  a {
    width: 100%;
    text-align: left;
  }

  .header-buttons {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin-left: auto;
  }

  .add-vitals-link-header {
    background: none;
    border: none;
    color: #3498db;
    text-decoration: underline;
    cursor: pointer;
    padding: 0.2rem 0.5rem;
    font-size: 0.9em;
    white-space: nowrap;
  }

  .add-vitals-link-header:hover {
    color: #2980b9;
  }
}
.card-body {
  padding: 0;
  font-size: 0.95rem;
}
.btn--remove {
  background-color: transparent;
  margin: 0;
  padding: 0;
  color: #ff4444;

  &:hover {
    color: #cc0000;
  }
}
.icon--remove {
  font-size: 1em;
}
.add-vitals-link {
  background: none;
  border: none;
  color: #3498db;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
}
.add-vitals-link:hover {
  color: #2980b9;
}
.mobile-vitals-empty,
.mobile-vitals-actions {
  margin-bottom: 0.75rem;
}
.mobile-vitals-actions .add-vitals-link-header {
  background: none;
  border: none;
  color: #3498db;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  font-size: 0.95rem;
}
.vitals-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  /* Above mobile sticky nav (z-index 2000500) so the title is not clipped. */
  z-index: 2000600;
  padding: 20px;
  overflow-y: auto;
}
.vitals-modal-content {
  background: white;
  border-radius: 12px;
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
}

@media (max-width: 768px) {
  .vitals-modal-overlay {
    /* Clear sticky header + Messages bar while keeping the dialog readable. */
    align-items: flex-start;
    padding: 5.25rem 0.75rem 4.75rem;
  }

  .vitals-modal-content {
    max-height: calc(100dvh - 5.25rem - 4.75rem);
  }
}
.vitals-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 25px;
  border-bottom: 2px solid #ecf0f1;
}
.vitals-modal-header h3 {
  margin: 0;
  color: #2c3e50;
  font-size: 1.3rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.modal-title-icon {
  color: #e74c3c;
}
.modal-close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #7f8c8d;
  cursor: pointer;
  padding: 5px 10px;
  border-radius: 4px;
  transition: all 0.2s ease;
}
.modal-close-btn:hover {
  background: #f0f0f0;
  color: #333;
}
.vitals-modal-body {
  padding: 1.5rem;
}
.vitals-form .form-group {
  margin-bottom: 1.5rem;
}
.vitals-form label {
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
}
.vitals-form input[type='number'],
.vitals-form input[type='datetime-local'],
.vitals-form textarea {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}
.vitals-form input:focus,
.vitals-form textarea:focus {
  outline: none;
  border-color: #3498db;
  box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
}
.bp-inputs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.bp-inputs input {
  flex: 1;
  min-width: 80px;
}
.bp-separator {
  font-size: 1.2rem;
  font-weight: 600;
  color: #666;
}
.bp-unit {
  color: #666;
  font-size: 0.9rem;
}
.vitals-form .form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #ecf0f1;
}
.btn-secondary {
  background: #ecf0f1;
  color: #2c3e50;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
}
.btn-secondary:hover {
  background: #d5dbdb;
}
.btn-primary {
  background: #3498db;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.btn-primary:hover:not(:disabled) {
  background: #2980b9;
}
.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
